import { useListEventRecordsQueryControllerList } from "@/generated/event-records/event-records";
import type { EventRecordListItemDto } from "@/generated/model/eventRecordListItemDto";
import type { ListEventRecordsQueryControllerListSortBy } from "@/generated/model/listEventRecordsQueryControllerListSortBy";
import type { ListEventRecordsQueryControllerListSortDir } from "@/generated/model/listEventRecordsQueryControllerListSortDir";
import type { SortDir } from "@/lib/category-display";
import {
  apiTabFor,
  type EventsHubSortColumn,
  type EventsHubTab,
} from "@/lib/events-hub";

interface UseEventsHubDataArgs {
  nurseryId?: string;
  tab: EventsHubTab;
  categoryId?: string;
  eventTypeId?: string;
  sortBy: EventsHubSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

export interface EventsHubCounts {
  open: number;
  awaitingAcknowledgement: number;
  history: number;
}

/**
 * Drives the Events Hub list server-side: forwards tab, category, event type, sort
 * and pagination, and reads back the page plus all three tab count badges. The
 * server computes the counts under the same filters, so a badge always matches
 * what switching to that tab would show.
 */
export function useEventsHubData({
  nurseryId,
  tab,
  categoryId,
  eventTypeId,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseEventsHubDataArgs) {
  const enabled = !!nurseryId;

  const listQuery = useListEventRecordsQueryControllerList(
    nurseryId ?? "",
    {
      tab: apiTabFor(tab),
      categoryId: categoryId || undefined,
      eventTypeId: eventTypeId || undefined,
      sortBy: sortBy as ListEventRecordsQueryControllerListSortBy,
      sortDir: sortDir as ListEventRecordsQueryControllerListSortDir,
      limit: pageSize,
      offset: page * pageSize,
    },
    // refetchOnMount "always": the global QueryClient defaults (staleTime 5min,
    // refetchOnMount false) would otherwise replay a cached page after a record is
    // acknowledged or resolved elsewhere, leaving a stale row in the wrong tab.
    { query: { enabled, refetchOnMount: "always" } },
  );

  const items: EventRecordListItemDto[] = enabled
    ? (listQuery.data?.data.data ?? [])
    : [];
  const total: number = enabled ? (listQuery.data?.data.meta.total ?? 0) : 0;
  const serverCounts = listQuery.data?.data.meta.counts;

  const counts: EventsHubCounts = {
    open: serverCounts?.open ?? 0,
    awaitingAcknowledgement: serverCounts?.awaitingAcknowledgement ?? 0,
    history: serverCounts?.history ?? 0,
  };

  return {
    items,
    total,
    isLoading: enabled && listQuery.isLoading,
    isError: listQuery.isError,
    counts,
  };
}
